import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

/**
 * Chat bot di assistenza — stesso contratto della landing
 * (landing_assistente.html): POST {API_BOT}/messaggio con
 * { messaggio, id_browser, operatore, id_chat, ads }.
 * Riusa la chiave localStorage 'id_browser' del sito: chat della landing,
 * chat in modale e modulo prova condividono lo stesso identificativo,
 * cosi' la guardia di idempotenza di registraProvaGratuita (ramo r.already)
 * riconcilia i due percorsi senza doppie registrazioni.
 */
/** Un messaggio già pronto per essere mostrato in chat. */
export interface MessaggioChat { da: 'io' | 'bot' | 'sistema'; testo: string; }

@Injectable({ providedIn: 'root' })
export class BotAssistenzaService {

  /**
   * L'indirizzo del bot segue l'ambiente, non è fisso: in locale punta al
   * backend che stai facendo girare tu, in produzione ad api.balin.app.
   * `communicationBaseUrl` è la stessa che usa tutto il resto dell'app, quindi
   * non c'è un secondo indirizzo da tenere allineato a mano.
   */
  static get API_BOT(): string {
    const base = (environment.communicationBaseUrl || '').replace(/\/+$/, '');
    return base + '/communication/site/v1/bot_assistenza';
  }
  static readonly ENDPOINT = '/messaggio';

  /** Il clic dell'annuncio salvato nel browser (chiave 'ads_clic', formato { v, t, url, tipo }). */
  private static clicSalvato(): any {
    try {
      const clic: any = JSON.parse(localStorage.getItem('ads_clic') || 'null');
      return clic && typeof clic.v === 'string' ? clic : null;
    } catch (err) {
      return null;
    }
  }
  static readonly FALLBACK = 'Ops, non riesco a rispondere in questo momento. Riprova tra poco oppure scrivi a info@balin.app.';
  private static readonly OPERATORI = ['Matteo', 'Umberto', 'Mattia', 'Marco', 'Danilo', 'Francesco', 'Leonardo'];

  idChat: string = null;
  status: string = 'BOT_ACTIVE';

  /** Identificativo condiviso col sito (common.js / prezzi.js / contattaci.js). */
  get idBrowser(): string {
    let id: string = null;
    try {
      id = localStorage.getItem('id_browser');
      if (!id) {
        id = Date.now() + (Math.random() + 1).toString(36).substring(7);
        localStorage.setItem('id_browser', id);
      }
    } catch (err) { id = 'volatile-' + Date.now(); }
    return id;
  }

  /**
   * Il nome con cui l'assistente si presenta. La chiave è `balin_operatore`,
   * la stessa che usa la landing: entrando in demo, DemoService ci riscrive
   * dentro il nome arrivato nell'URL, così il cliente ritrova la persona con
   * cui stava già parlando invece di trovarne un'altra.
   */
  get operatore(): string {
    let op: string = null;
    try {
      op = localStorage.getItem('balin_operatore');
      if (!op) {
        op = BotAssistenzaService.OPERATORI[Math.floor(Math.random() * BotAssistenzaService.OPERATORI.length)];
        localStorage.setItem('balin_operatore', op);
      }
    } catch (err) { op = BotAssistenzaService.OPERATORI[0]; }
    return op;
  }

  /** Invia un messaggio; risolve { risposta, troncata, passatoAOperatore, errore }. */
  async invia(messaggio: string, ads?: any): Promise<{ risposta?: string; troncata?: boolean; passatoAOperatore?: string; errore?: string }> {
    const corpo: any = { messaggio, id_browser: this.idBrowser, operatore: this.operatore };
    if (this.idChat) corpo.id_chat = this.idChat;
    // Il clic dell'annuncio: se non viene passato, quello salvato all'ingresso in
    // /demo o dallo stepper. Il bot lo tiene solo quando la conversazione nasce,
    // quindi va mandato anche da qui: chi scrive per la prima volta nella demo
    // altrimenti aprirebbe una conversazione senza clic, e non si recupera più.
    const clic = ads || BotAssistenzaService.clicSalvato();
    if (clic) corpo.ads = clic;
    try {
      const r = await fetch(BotAssistenzaService.API_BOT + BotAssistenzaService.ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(corpo)
      });
      const d: any = await r.json();
      if (d.id_chat) this.idChat = d.id_chat;
      if (d.status) this.status = d.status;
      if (d.risposta) return { risposta: d.risposta, troncata: !!d.troncata };
      if (d.status && d.status !== 'BOT_ACTIVE') return { passatoAOperatore: d.status };
      return { errore: d.description || JSON.stringify(d) };
    } catch (err) {
      return { errore: BotAssistenzaService.FALLBACK };
    }
  }

  /**
   * Riprende la conversazione esistente del visitatore (GET /conversazione).
   *
   * Il backend manda i messaggi nella forma della collection bot_message —
   * `{ content, direction: 'inbound' | 'outbound', timestamp_created }` — che
   * non è quella che serve a una chat. La traduzione si fa qui, una volta
   * sola: chi chiama riceve già `{ da, testo }` e non deve sapere com'è fatto
   * il database.
   */
  async conversazione(): Promise<{ id_chat?: string; status?: string; messaggi: MessaggioChat[] }> {
    try {
      const r = await fetch(BotAssistenzaService.API_BOT + '/conversazione?id_browser=' + encodeURIComponent(this.idBrowser));
      const d: any = await r.json();
      if (d && d.id_chat) this.idChat = d.id_chat;
      if (d && d.status) this.status = d.status;
      const grezzi: any[] = (d && d.messaggi) || [];
      return {
        id_chat: d?.id_chat,
        status: d?.status,
        messaggi: grezzi
          .map((m: any) => ({
            da: (m.direction === 'inbound' ? 'io' : 'bot') as ('io' | 'bot'),
            testo: m.content || ''
          }))
          .filter(m => !!m.testo)
      };
    } catch (err) { return { messaggi: [] }; }
  }
}
